import PropTypes from "prop-types";
import React from "react";

// Defaults
import { DefaultCommissionsTier, OperatingCostsCommissionTier, OperatingCostsMakesDefinition } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import { getErrorKey } from "@/Common/helpers/Modifiers/characters";
import { getMatrixDimensions, getExactMatrixDimensions } from "@/Common/helpers/Matrices";
import { removeListValue } from "@/Common/helpers/Arrays";

// Layouts
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";

// Custom components
// import Content from "./Content";

// MUI
import { Grid } from "@mui/material";
import RenderContent from "./RenderContent";

// Constants
const INPUT_MAX_TIER = 500000000;
const INPUT_MAX_PERCENTAGE = 100;
const TOTAL_TYPE = "Total Operating Cost";

const mapDefinitionsToArray = (definition = null) => {
	if (!definition) {
		return [];
	}

	const result = [];
	for (const [key, value] of Object.entries(definition)) {
		const valueType = (typeof value).toUpperCase();
		if (key && !Array.isArray(value) && valueType === "OBJECT") {
			result.push(value);
		}
	}
	return result;
};

const filterErrorsList = (errors, key) => errors.filter((error) => error !== key);

const getOperatingCostsMakeTabs = (makes = []) => makes?.map((element) => element?.make || TOTAL_TYPE);

const getCombinedMakes = (args) => {
	const { content, makes = [], oems = [], combineOEMs = false } = args;
	const combined = [];

	if (combineOEMs) {
		const isNewIncluded = makes?.some((element) => element.make === TOTAL_TYPE);
		if (!isNewIncluded) {
			combined.push(
				OperatingCostsMakesDefinition(TOTAL_TYPE, "", {
					tiers: [
						DefaultCommissionsTier(INPUT_MAX_TIER, 0, [
							OperatingCostsCommissionTier(INPUT_MAX_PERCENTAGE, -INPUT_MAX_PERCENTAGE, { commission: 0, input: "Percent" }),
						]),
					],
				})
			);
		}
	} else {
		const contentOriginalList = mapDefinitionsToArray(content);
		if (oems?.length > 1) {
			oems.forEach((oem) => {
				const oemName = oem?.oemDisplayName ?? oem.oemName;
				combined.push(
					OperatingCostsMakesDefinition(oemName, oem.dealerCodeFS, {
						tiers: [
							DefaultCommissionsTier(INPUT_MAX_TIER, 0, [
								OperatingCostsCommissionTier(INPUT_MAX_PERCENTAGE, -INPUT_MAX_PERCENTAGE, {
									commission: 0,
									input: "Percent",
								}),
							]),
						],
					})
				);
			});
		} else {
			combined.push(makes[0]);
		}
		contentOriginalList?.forEach((element) => {
			const bonusToKeep = makes.find((bonus) => bonus.make === element.make);
			if (bonusToKeep) {
				combined.push(bonusToKeep);
			}
		});
	}

	return combined;
};

const getPotentialTabsErrorsKeys = (section, tabs = []) => tabs?.map((tab) => getErrorKey(section, tab));

const getErrorsToRemove = (errors = [], errorsToKeep = []) => {
	if (errors.length) {
		return errors.filter((key) => !errorsToKeep.includes(key));
	}
	return [];
};

const mapModelToPropagate = (makes = [], tabs = null) => {
	return {
		makes,
		combineOEMs: tabs?.combineOEMs,
	};
};

export default function OperatingCosts(props) {
	const {
		sectionId = "operatingCosts",
		content = {
			makes: [],
			combineOEMs: false,
		},
		oems = [],
		loading = false,
		readOnly = false,
		onActionUpdate = () => {},
		useSectionsErrors = () => {},
	} = props;

	// Hooks
	const { errors, addSectionError, addSectionErrors, removeSectionError, removeSectionErrors, clearSectionErrors } =
		useSectionsErrors?.() || {};

	// States
	const [model, setModel] = React.useState(null);
	const [tabIndex, setTabIndex] = React.useState(0);
	const preventSave = React.useRef([]);

	const matrixDimensions = getMatrixDimensions("commissions", model?.makes || []);

	// Error handler
	const tabsErrors = React.useMemo(() => {
		if (errors?.size && model?.tabsProps?.tabs) {
			const localErrors = Array.from(Array(model.tabsProps.tabs.length)).map(() => false);
			const errorsToKeep = getPotentialTabsErrorsKeys(sectionId, model.tabsProps.tabs);
			const listErrors = errors?.get(sectionId) || []; // Ids that have errors

			errorsToKeep.forEach((errorKey, index) => {
				const included = listErrors.includes(errorKey);
				localErrors[index] = Boolean(included);
			});

			return localErrors;
		}
		return [];
	}, [sectionId, errors, model?.tabsProps?.tabs]);

	// Actions handlers
	const update = (section, data, area) => {
		const filteredErrors = filterErrorsList(errors?.get(section) || [], getErrorKey(section, area));
		const isValid = !filteredErrors?.length;

		if (isValid && !preventSave?.current?.length) {
			const validatedMakes = data?.makes?.map((make) => {
				const isEnabledFinance = make.isFinance && Boolean(make.tiers?.length > 1);
				return {
					...make,
					isFinance: isEnabledFinance,
				};
			});

			const modelToPropagate = mapModelToPropagate(validatedMakes, data.tabsProps);
			onActionUpdate?.(section, null, modelToPropagate, isValid);
		} else {
			setModel(clone(data));
		}
	};

	const handleUpdateTiers = (section, area, data) => {
		const updatedModel = clone(model);
		const makes = [...updatedModel.makes];

		const currentMatrixDimensions = getExactMatrixDimensions("commissions", data);
		matrixDimensions.set(area, currentMatrixDimensions);
		preventSave.current = removeListValue(preventSave.current, getErrorKey(sectionId, area));
		removeSectionError(section, area);

		// Search make and update
		makes?.forEach((element, key) => {
			if (element.make === area) {
				makes[key]["tiers"] = data;
			}
		});

		update(section, updatedModel, area);
	};

	const handleUpdateSwitches = (section, area, value, tabIndexSwitch = null) => {
		const updatedModel = { ...model };

		switch (area) {
			case "applyTiersRetroactively":
			case "frequency":
				if (isNumeric(tabIndexSwitch)) {
					updatedModel["makes"][tabIndexSwitch][area] = value;
				}
				break;
			default:
				updatedModel["tabsProps"][area] = value;
				break;
		}

		const combineOEMs = updatedModel["tabsProps"]["combineOEMs"];

		if (area === "combineOEMs") {
			const makes = getCombinedMakes({ makes: updatedModel["makes"], oems, content, combineOEMs });
			const tabsProps = { ...updatedModel["tabsProps"], tabs: getOperatingCostsMakeTabs([...makes]) };
			updatedModel["tabsProps"] = tabsProps;
			updatedModel["makes"] = makes;
			resetTabsErrors(section, updatedModel["tabsProps"]["tabs"]);
		}

		update(section, updatedModel, area);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	const resetTabsErrors = React.useCallback(
		(resetSectionId, tabs) => {
			if (tabs?.length) {
				const errorsToKeep = getPotentialTabsErrorsKeys(resetSectionId, tabs);
				const errorsToRemove = getErrorsToRemove(errors?.get(resetSectionId), errorsToKeep);
				removeSectionErrors(resetSectionId, errorsToRemove);
				addSectionErrors(resetSectionId, errorsToKeep);
			}
		},
		[errors, removeSectionErrors, addSectionErrors]
	);

	React.useEffect(() => {
		const additionalMakes = mapDefinitionsToArray(content);
		const initialMakes = [...content.makes, ...additionalMakes];
		const makes = clone([...initialMakes]);

		setModel({
			tabsProps: {
				tabs: getOperatingCostsMakeTabs([...makes]),
				combineOEMs: Boolean(content.combineOEMs),
				errors: [],
			},
			makes,
		});

		return () => {
			setModel(null);
			preventSave.current = [];
		};
	}, [content]);

	React.useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
		};
	}, [sectionId, clearSectionErrors]);

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					Boolean(model?.makes?.length) && (
						<TabSelectable
							tabs={model?.tabsProps.tabs || []}
							onTabChange={(index) => setTabIndex(index)}
							errors={tabsErrors}
							content={Object.entries(model?.makes)?.map(([key, value]) => {
								return (
									<RenderContent
										key={`${key}_${value.dealerCodeFS}`}
										model={model}
										tabIndex={tabIndex}
										sectionId={sectionId}
										readOnly={readOnly}
										value={value}
										handleUpdateSwitches={handleUpdateSwitches}
										handleUpdateTiers={handleUpdateTiers}
										handleValidation={handleValidation}
									/>
								);
							})}
							topActions={[
								<CustomizedSwitches
									key={"combineOEMs"}
									textLeft={true}
									textChecked={"Combine OEMs"}
									textUnChecked={"Combine OEMs"}
									checked={Boolean(model?.tabsProps?.combineOEMs)}
									disabled={readOnly || oems?.length < 2}
									onChange={(value) => handleUpdateSwitches(sectionId, "combineOEMs", value)}
									tooltip={{
										title: `Turns all oems tabs into a ${TOTAL_TYPE} tab.`,
										placement: "bottom",
										key: "growth-bonus-combine-franchises-tooltip",
									}}
								/>,
							]}
							bottomActions={[
								<CustomizedSwitches
									key={"applyTiersRetroactively"}
									textLeft={true}
									textChecked={"Apply retroactively"}
									textUnChecked={"Apply retroactively"}
									checked={Boolean(model?.makes?.[tabIndex]?.applyTiersRetroactively)}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "applyTiersRetroactively", value, tabIndex)}
								/>,
							]}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
OperatingCosts.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.shape({
		makes: PropTypes.array,
		combineOEMs: PropTypes.bool,
	}),
	oems: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	useSectionsErrors: PropTypes.func,
};
